import { Control, useController, useWatch } from 'react-hook-form';
import { VariableFormItem } from '../data/useProjectData/useProjectData';
import { isRuleSatisfied } from '../utils/dependencyRules';
import { Entry, isVisibilityDependencyRule } from '../types';
import { useCallback, useEffect, useMemo } from 'react';
import { isEqual } from 'lodash';
import { backendValueToFormValue } from '../utils/parse-backend-values/backendValueToFormValue';
import clsx from 'clsx';
import { useSetAutoClearedFields } from '../AutoClearedFieldsContext';

export const useVariableVisibilityFunction = ({
	control,
	variableFormItems
}: {
	control: Control<Entry>;
	variableFormItems: VariableFormItem[];
}) => {
	const names = new Set<string>();
	variableFormItems.forEach(item => {
		item.dependencies
			.filter(isVisibilityDependencyRule)
			.forEach(rule => names.add(rule.supplierVariable.variableName));
	});
	const allSupplierNames = Array.from(names);

	const supplierValues = useWatch({ control, name: allSupplierNames });

	const supplierValueMap = useMemo(() => {
		const map: Record<string, string | string[]> = {};
		allSupplierNames.forEach((name, index) => {
			const value = supplierValues[index];
			if (Array.isArray(value)) {
				map[name] = value as string[];
			} else {
				map[name] = (value ?? '') as string;
			}
		});
		return map;
	}, [allSupplierNames, supplierValues]);

	const isVariableVisible = useCallback(
		(item: VariableFormItem): boolean => {
			const visibilityDependencies = item.dependencies.filter(isVisibilityDependencyRule);
			if (visibilityDependencies.length === 0) return true;
			return visibilityDependencies.some(rule =>
				isRuleSatisfied({
					rule,
					supplierValue: supplierValueMap[rule.supplierVariable.variableName] ?? ''
				})
			);
		},
		[supplierValueMap]
	);

	return { isVariableVisible };
};

export const VisibilityRuleTrigger = ({
	variableFormItem: item,
	children,
	control
}: {
	variableFormItem: VariableFormItem;
	children: React.ReactNode;
	control: Control<Entry>;
}) => {
	const visibilityDependencies = item.dependencies.filter(isVisibilityDependencyRule);
	const form = useController({ control, name: item.variable.variableName });
	const supplierValues = useWatch({
		control,
		name: visibilityDependencies.map(rule => rule.supplierVariable.variableName)
	});

	const { setField } = useSetAutoClearedFields();

	const isVisible =
		visibilityDependencies.length === 0 ||
		visibilityDependencies.some((rule, index) =>
			isRuleSatisfied({ rule, supplierValue: supplierValues[index] })
		);

	useEffect(() => {
		if (item.variable.entryType === 'calculated') {
			return;
		}
		const emptyValue = backendValueToFormValue({ value: null, variable: item.variable });

		// we don't delete the field because we want the user to be aware of the changes we've done as they are permanent
		if (!isVisible && !isEqual(form.field.value, emptyValue)) {
			form.field.onChange(emptyValue);
			setField(item.variable.variableName, {
				from: form.field.value,
				to: emptyValue,
				variable: item.variable,
				becauseRules: visibilityDependencies.filter(
					(rule, index) =>
						!isRuleSatisfied({ rule, supplierValue: supplierValues[index] })
				)
			});
		}
	}, [isVisible]);

	if (!isVisible) {
		return null;
	}

	return (
		<div
			data-scroll-anchor
			id={`scroll-anchor_${item.variable.variableName}`}
			className={clsx(
				'col-span-full self-end',

				item.columnSpan === 1 && 'md:col-span-1',
				item.columnSpan === 2 && 'md:col-span-2',

				item.columnStart === 1 && 'md:col-start-1',
				item.columnStart === 2 && 'md:col-start-2',

				item.hideFromUi && 'hidden'
			)}
		>
			{children}
		</div>
	);
};
